import { Command, Option } from "clipanion";
import { DefaultAzureCredential } from "@azure/identity";
import { checkAppHealth, readAppSecrets } from "@m8t-stack/github-app-auth";
import { M8tCommand } from "../../lib/m8t-command.js";
import { LocalCliError } from "../../lib/errors.js";
import { colors, renderJson, resolveOutputMode } from "../../lib/output.js";
import { resolveFoundryProject } from "../../lib/foundry-project.js";
import { getAzAccount } from "../../lib/auth.js";
import { readAgentYaml } from "../../lib/agent-yaml.js";
import { isGhAuthed, ghAuthLoginDevice } from "../../lib/gh-helpers.js";
import { resolveAppInstallation } from "../../lib/app-install.js";
import { linkBrain } from "../../lib/brain-link.js";
import { discoverKvUri, resolveRepoRoot } from "../../lib/brain-paths.js";

export class BrainLinkCommand extends M8tCommand {
  static paths = [["brain", "link"]];
  static usage = Command.Usage({
    description: "Link a worker to an existing brain repo. Idempotent.",
    details:
      "Installs the GitHub App on <repo> (browser click + poll), mints an installation token + PATCHes the Foundry connection, deploys a new agent version with the brain loader + tools[type:mcp] + metadata.brain JSON string, and pushes .m8t/brain.yaml to the repo. F1's PAT mode keeps working untouched. --force re-runs the full link cascade (loader re-render + createVersion + brain.yaml mirror) even when metadata.brain already matches — the F1 rescue path.",
    examples: [
      ["Link cmo to orkeren21/cmo-brain", "$0 brain link cmo --repo orkeren21/cmo-brain"],
      ["Force a re-link (re-render brain loader + new agent version, even if already linked)", "$0 brain link cmo --repo orkeren21/cmo-brain --force"],
    ],
  });

  worker = Option.String();
  repo = Option.String("--repo");
  branch = Option.String("--branch");
  kvUri = Option.String("--kv-uri");
  subscription = Option.String("--subscription");
  endpoint = Option.String("--endpoint");
  output = Option.String("--output");
  modelDeployment = Option.String("--model-deployment");
  personaPath = Option.String("--persona");
  allowNonReasoning = Option.Boolean("--allow-non-reasoning", false);
  force = Option.Boolean("--force", false);

  async executeCommand(): Promise<number> {
    const repo = typeof this.repo === "string" ? this.repo : undefined;
    if (!repo) throw new LocalCliError({ code: "USAGE", message: "--repo is required (owner/name)" });
    if (!repo.includes("/")) throw new LocalCliError({ code: "USAGE", message: `--repo must be owner/name, got '${repo}'` });
    const branch = this.branch ?? "main";
    const env = this.context.env;
    const kvUri = discoverKvUri(env, typeof this.kvUri === "string" ? this.kvUri : undefined);

    // Normalize output: clipanion leaves a descriptor object on the property
    // until argv parsing; when set directly in tests (or not supplied), treat
    // anything that is not a plain string as "pretty" (our diagnostic default).
    const outputFlag =
      this.output === "json" || this.output === "auto" || this.output === "pretty"
        ? (this.output)
        : "pretty";
    const mode = resolveOutputMode(outputFlag, this.context.stdout as NodeJS.WriteStream);
    const progress = mode === "pretty" ? (m: string) => this.context.stderr.write(`  ${colors.dim(m)}\n`) : undefined;

    const credential = new DefaultAzureCredential();

    // Pre-flight 1: gh auth
    if (!(await isGhAuthed())) {
      this.context.stdout.write(`${colors.hint("note:")} gh not authed — running gh auth login --device --scopes "repo,read:org"\n`);
      await ghAuthLoginDevice();
    }

    // Pre-flight 2: inline check-app (silent if pass)
    const health = await checkAppHealth({ credential, kvUri });
    if (!health.ok) {
      throw new LocalCliError({
        code: "APP_HEALTH_FAILED",
        message: "GitHub App is not configured correctly.",
        hint: "Run `m8t brain check-app` for details, then re-run this command.",
      });
    }

    // Pre-flight 3: resolve Foundry project (for ARM id)
    const account = await getAzAccount();
    const subscriptionId =
      (typeof this.subscription === "string" ? this.subscription : undefined) ??
      account.subscriptionId;
    const agentEndpoint =
      typeof this.worker === "string" ? readAgentYaml(this.worker)?.projectEndpoint : undefined;
    const project = await resolveFoundryProject({
      credential,
      subscriptionId,
      interactive: (this.context.stdout as NodeJS.WriteStream).isTTY === true,
      endpoint: this.endpoint,
      agentEndpoint,
    });

    // App install: probe-then-open + poll
    const [owner, repoName] = repo.split("/");
    const { slug, appId, privateKeyPem } = await readAppSecrets({ credential, kvUri });
    // Probe-then-open: skip browser when the App is already installed on this repo
    // (Ordering 3 idempotent re-link). resolveAppInstallation handles both paths.
    const state = { alreadyInstalled: false };
    const installationId = await resolveAppInstallation({
      owner, repo: repoName, slug, appId, privateKeyPem,
      write: (s) => {
        this.context.stdout.write(`${colors.field("→")} ${s}`);
        this.context.stderr.write(`  ${colors.dim("waiting for installation (Ctrl-C to cancel)…")}\n`);
      },
      onAlreadyInstalled: (id) => {
        state.alreadyInstalled = true;
        this.context.stdout.write(`  ${colors.success("✓")} App already installed on ${colors.field(repo)} (installation ${id}); skipping browser open.\n`);
      },
      onTick: (n) => { if (n % 5 === 0) this.context.stderr.write(`  ${colors.dim(`…still waiting (${(n * 2).toString()}s)`)}\n`); },
    });
    if (!state.alreadyInstalled) {
      this.context.stdout.write(`  ${colors.success("✓")} installation detected (id: ${installationId})\n`);
    }

    // The convergent link cascade
    const result = await linkBrain({
      credential, kvUri,
      projectEndpoint: project.endpoint,
      projectArmId: `${project.accountScope}/projects/${project.projectName}`,
      agentName: this.worker,
      repo, branch,
      repoRoot: resolveRepoRoot(),
      installationId,
      // Clipanion leaves a truthy Option descriptor here until argv is parsed
      // (tests set the literal boolean). `!== true` keeps the idempotent default
      // when --force is absent; `!this.force` would misread the descriptor as set.
      skipIfLinked: this.force !== true,
      onProgress: progress,
      subscriptionId,
      project,
      modelDeployment: typeof this.modelDeployment === "string" ? this.modelDeployment : undefined,
      personaPathOverride: typeof this.personaPath === "string" ? this.personaPath : undefined,
      // `=== true` distinguishes an explicitly-passed flag from the truthy
      // clipanion Option descriptor (which would otherwise enable it by default).
      allowNonReasoning: this.allowNonReasoning === true,
    });

    if (mode === "json") {
      this.context.stdout.write(renderJson({
        worker: this.worker, repo, branch,
        installationId: result.installationId,
        connectionName: result.connectionName,
        foundryVersion: result.foundryVersion,
        alreadyLinked: result.alreadyLinked,
      }) + "\n");
      return 0;
    }

    if (result.alreadyLinked) {
      this.context.stdout.write(`${colors.success("✓")} ${colors.field(this.worker)} already linked to ${colors.field(repo)} (token rotated). No-op.\n`);
    } else {
      this.context.stdout.write(`${colors.success("✓")} linked ${colors.field(this.worker)} ↔ ${colors.field(repo)} via App-mode (install ${installationId}, agent version ${result.foundryVersion ?? "unknown"}).\n`);
      this.context.stdout.write(`  ${colors.hint("brain doctrine:")} https://github.com/${repo}/blob/${branch}/AGENTS.md\n`);
      this.context.stdout.write(`  ${colors.hint("token:")} rotates lazily on every invoke (60min TTL, 10min safety margin).\n`);
    }
    return 0;
  }
}
